import React, { useContext } from 'react';
import * as _ from 'lodash';
import { isSameWeek, subDays, getTime, isSameDay, isAfter } from 'date-fns';
import { InfoListItem } from '@brightlayer-ui/react-components/core';
import * as PXBColors from '@brightlayer-ui/colors';
import { Divider } from '@mui/material';
import { useTimelineData, useTimelineFetchMore } from '../../data/DataProviders/TimelineDataProvider';
import { isDataLoaded, Data, DataState, isDataLoading, isDataError } from '../../types/data-providers';
import { DataIndicators } from '../DataIndicators';
import { TimelineEventListGroups } from '../TimelineEventListGroups';
import { TimelineListItem } from '../TimelineListItem';
import { AppRoute } from '../../routes';
import { useLanguageLocale, LanguageLocale } from '../../hooks/language-locale-hooks';
import { DataLoadingIndicator } from '../DataLoadingIndicator';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { Clock } from '../../lib/clock';
import { ExportPopUp } from '../ExportPopUp/ExportPopUp';
import { PopupContext } from '../../data/DataProviders/TimelinePopupProvider';
import { useSignalRData } from '../../data/DataProviders/SignalRDataProvider';
import { useSelectedLocationData } from '../../hooks/data-hooks';
import { getRecentTimelineData } from '../../lib/signalr-helper';
import { TimelineCardEvent } from '../TimelineListItem/types';
import { Footer } from '../Footer/Footer';

const isLastWeek = (date: Date): boolean => isSameWeek(date, subDays(Clock.now(), 7));
const isThisWeek = (date: Date): boolean => isSameWeek(date, Clock.now());
const isYesterday = (date: Date): boolean => isSameDay(date, subDays(Clock.now(), 1));
const isFuture = (date: Date): boolean => isAfter(date, Clock.now());
const isToday = (date: Date): boolean => isSameDay(date, Clock.now());

const getRelativeDate = (dateTime: string): string => {
    const date = new Date(dateTime);

    if (isToday(date)) {
        return 'DASHBOARD.TIMELINE_DETAILS.TODAY';
    }

    if (isFuture(date)) {
        return 'DASHBOARD.TIMELINE_DETAILS.FUTURE';
    }

    if (isYesterday(date)) {
        return 'DASHBOARD.TIMELINE_DETAILS.YESTERDAY';
    }

    if (isThisWeek(date)) {
        return 'DASHBOARD.TIMELINE_DETAILS.THIS_WEEK';
    }

    if (isLastWeek(date)) {
        return 'DASHBOARD.TIMELINE_DETAILS.LAST_WEEK';
    }

    return 'DASHBOARD.TIMELINE_DETAILS.EARLIER';
};

function makeGroups<TItem>(items: TItem[], groupFn: (item: TItem) => string): Array<{ name: string; items: TItem[] }> {
    const groups = _.groupBy(items, groupFn);

    return Object.keys(groups).map((groupName) => ({
        name: groupName,
        items: groups[groupName],
    }));
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getRemainingGroups = (args: {
    languageLocale: LanguageLocale;
    errorInMostRecentData: boolean;
    group:
        | {
              name: string;
              items: TimelineCardEvent[];
          }
        | undefined;
    showMoreRecords: boolean;
    onClickShowMoreRecords: () => void;
}) => {
    if (!args.group) {
        return [];
    }

    return [
        {
            name: args.group.name,
            items: args.group.items,
            footerComponent: (
                <div>
                    <Divider />
                    {args.showMoreRecords && (
                        <InfoListItem
                            style={{
                                color: PXBColors.blue[500],
                                textAlign: 'center',
                            }}
                            onClick={(): void => {
                                args.onClickShowMoreRecords();
                            }}
                            title={args.languageLocale.t('EVENT_TIMELINE.LOAD_MORE_EVENTS', {
                                count: 50,
                            })}
                        ></InfoListItem>
                    )}
                    {args.errorInMostRecentData && (
                        <DataErrorIndicator
                            data={{ state: DataState.Error, errorMessage: '' }}
                            message={args.languageLocale.t('EVENT_TIMELINE.ERROR_LOADING_MORE_EVENTS')}
                            inline
                        />
                    )}
                </div>
            ),
        },
    ];
};

export const TimelineEvents: React.FC<{ appRoute: AppRoute; showPath?: boolean; disableDownload?: Function }> = (props) => {
    const languageLocale = useLanguageLocale();
    const timelineDataset = useTimelineData();
    const fetchMoreTimeline = useTimelineFetchMore();
    const popUpAction = useContext(PopupContext);
    const signalRData = useSignalRData();
    const selectedLocationData = useSelectedLocationData();
    const updateTimeline = (
        newTimeline: TimelineCardEvent[],
        existingRecords: TimelineCardEvent[]
    ): TimelineCardEvent[] => {
        _.filter(newTimeline, (data) => {
            _.filter(existingRecords, (timeline) => {
                if (timeline.alarmId === data.alarmId) timeline.status = data.status;
                else {
                    if (existingRecords.every((arr) => arr.alarmId !== data.alarmId)) existingRecords.push(data);
                }
            });
        });

        return existingRecords;
    };

    const timelineData: Data<{
        timeline: TimelineCardEvent[];
        moreRecords: boolean;
        isLoadingMore: boolean;
        errorInMostRecentData: boolean;
       
    }> = React.useMemo(() => {
        if (timelineDataset.every((ds) => isDataLoading(ds))) {
            return {
                state: DataState.Loading,
            };
        }

        if (timelineDataset.every((ds) => isDataError(ds))) {
            return {
                state: DataState.Error,
                errorMessage: 'ERROR!',
            };
        }

        let records = _.flatten(
            timelineDataset.map((dataset) => {
                if (isDataLoaded(dataset)) return dataset.value.timeline;

                return [];
            })
        );
        if (signalRData.locations.length > 0) {
            const selectedSiteId =
                props.appRoute.type === 'OverviewSiteTimelineEventDetails' ? props.appRoute.siteId : '';
            const newTimeline = getRecentTimelineData(signalRData, selectedLocationData, selectedSiteId);
            if (newTimeline.length > 0) {
                records = updateTimeline(newTimeline, records);
                if (timelineDataset.length > 0 && timelineDataset[0].state === DataState.Loaded)
                    timelineDataset[0].value.timeline = records;
            }
        }

        const mostRecentlyRequestedTimelineData = timelineDataset[timelineDataset.length - 1];
        const moreRecords =
            isDataLoaded(mostRecentlyRequestedTimelineData) &&
            mostRecentlyRequestedTimelineData.value.paging.moreRecords;

        const isLoadingMore = isDataLoading(mostRecentlyRequestedTimelineData);

        const errorInMostRecentData = isDataError(mostRecentlyRequestedTimelineData);
        return {
            state: DataState.Loaded,
            value: {
                timeline: records,
                moreRecords,
                isLoadingMore,
                errorInMostRecentData,
            },
        };
    }, [timelineDataset, signalRData]); // eslint-disable-line react-hooks/exhaustive-deps

    const isEmpty = isDataLoaded(timelineData) && timelineData.value.timeline.length === 0;

    React.useEffect(() => {
       if(isDataLoaded(timelineData) && props.disableDownload){
            const isdisable = (timelineData.value.timeline.length === 0);
            props.disableDownload(isdisable);

       } 


    },[timelineData])

    const events = isDataLoaded(timelineData)
        ? _.orderBy(timelineData.value.timeline, (event) => getTime(new Date(event.dateTime)), 'desc')
        : [];

    const groups = makeGroups(events, (event) => languageLocale.t(getRelativeDate(event.dateTime)));

    const earlierGroupName = groups.length > 0 ? groups[groups.length - 1].name : '';

    const recentGroups = groups.filter((group) => group.name !== earlierGroupName);
    const earlierGroup = groups.find((group) => group.name === earlierGroupName);
    const remainingGroups = getRemainingGroups({
        languageLocale,
        group: earlierGroup,
        showMoreRecords: isDataLoaded(timelineData) && timelineData.value.moreRecords,
        onClickShowMoreRecords: fetchMoreTimeline,
        errorInMostRecentData: isDataLoaded(timelineData) && timelineData.value.errorInMostRecentData,
    });

    const allGroups = [...recentGroups, ...remainingGroups];
    const onClose = (): void => {
        if (popUpAction) popUpAction.openPopup(false);
    };
    return (
        <>
            <DataIndicators
                errorMessage={languageLocale.t('DASHBOARD.ERROR_LOADING_EVENTS')}
                emptyMessage={languageLocale.t('DASHBOARD.NO_EVENTS')}
                data={timelineData}
                emptyIcon="NotificationsActive"
                isEmpty={isEmpty}
            />
            {isDataLoaded(timelineData) && (
                <>
                    <TimelineEventListGroups
                        groups={allGroups}
                        component={TimelineListItem}
                        appRoute={props.appRoute}
                        options={{ showDeviceName: true, showPath: props.showPath, showBadges: true }}
                        componentTestId="timeline-list-item"
                    />
                    {timelineData.value.timeline.length > 0 && (
                        <Footer text={languageLocale.t('DISCLAMIER.TIMELINE')} />
                    )}
                </>
            )}

            {popUpAction && popUpAction.open && (
                <ExportPopUp for="Timeline" open={popUpAction.open} onClose={onClose}></ExportPopUp>
            )}

            {isDataLoaded(timelineData) && timelineData.value.isLoadingMore && (
                <DataLoadingIndicator data={{ state: DataState.Loading }} inline />
            )}
        </>
    );
};
